import React, { useState, useEffect } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { usePrimaryFacility, useCurrentUser } from '../../hooks/useCache';
import { ClockEventService } from '../../services/clockEventService';
import ClockInOutModal from './ClockInOutModal';
import { 
  Snackbar, 
  Alert 
} from '@mui/material';

// Inner component that only renders when facilities are loaded
const AutoClockInManagerInner = ({ user, primaryFacility }) => {
  const [showAutoClockModal, setShowAutoClockModal] = useState(false);
  const [shiftInfo, setShiftInfo] = useState(null);
  const [hasChecked, setHasChecked] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Now we can safely call useFeatureFlag because we know primaryFacility exists
  const { isEnabled: schedulingEnabled, loading: featureLoading } = useFeatureFlag(
    'sch', 
    primaryFacility.id
  );

  // Auto check for clock-in requirement when user logs in
  useEffect(() => {
    const checkAutoClockIn = async () => {
      // Don't proceed without proper facility context and feature flag resolution
      if (!user?.uid || !schedulingEnabled || hasChecked || featureLoading) {
        return;
      }

      console.log('Checking if user needs to clock in automatically...');
      
      try {
        const clockInCheck = await ClockEventService.checkClockInRequired(user.uid, primaryFacility.id);
        
        console.log('Clock-in check result:', clockInCheck);
        
        if (clockInCheck.shouldPrompt) {
          setShiftInfo(clockInCheck.shiftInfo);
          setShowAutoClockModal(true);
          setSnackbar({
            open: true,
            message: 'You have a scheduled shift. Please clock in.',
            severity: 'info'
          });
        } else {
          console.log('Auto clock-in not needed:', clockInCheck.reason);
        }
      } catch (error) {
        console.error('Error checking auto clock-in:', error);
      } finally {
        setHasChecked(true);
      }
    };

    // Only run this check once per login session when we have proper facility context
    if (user && primaryFacility && schedulingEnabled && !featureLoading) {
      checkAutoClockIn();
    }
  }, [user, primaryFacility, schedulingEnabled, featureLoading, hasChecked]);

  const handleAutoClockSuccess = async (action) => {
    const message = action === 'clock_in' 
      ? 'Successfully clocked in! You now have access to the EMR system.'
      : 'Successfully clocked out! You will be signed out shortly.';
      
    setSnackbar({
      open: true,
      message,
      severity: 'success'
    });
    setShowAutoClockModal(false);
    
    // Immediately dispatch event with status information for instant UI updates
    const newClockStatus = action === 'clock_in';
    console.log('🚀 Auto clock-in: Dispatching immediate clock status update:', newClockStatus);
    
    window.dispatchEvent(new CustomEvent('clockStatusUpdate', {
      detail: { 
        isClockedIn: newClockStatus,
        action: action,
        timestamp: new Date().toISOString(),
        source: 'auto-clock'
      }
    }));
    
    // Notify parent component to refresh clock-in status (legacy event)
    window.dispatchEvent(new CustomEvent('clockInStatusChanged'));
    
    console.log('📢 Auto clock-in: Clock status change events dispatched');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleModalClose = () => {
    setShowAutoClockModal(false);
    // Show a gentle reminder that they can clock in later
    setSnackbar({
      open: true,
      message: 'You can clock in anytime from the dashboard.',
      severity: 'info'
    });
  };

  return (
    <>
      <ClockInOutModal
        open={showAutoClockModal}
        onClose={handleModalClose}
        user={user}
        facilityId={primaryFacility.id}
        action="clock_in"
        onSuccess={handleAutoClockSuccess}
        isAutoPrompt={true}
        shiftInfo={shiftInfo}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const AutoClockInManager = () => {
  const { user, isLoaded: userLoaded } = useCurrentUser();
  const { primaryFacility, isLoaded: facilityLoaded } = usePrimaryFacility();

  // Only render the inner component when user and facility are loaded
  if (!userLoaded || !facilityLoaded || !primaryFacility || !primaryFacility.id) {
    // Don't log this as an error since it's expected during initialization
    return null;
  }

  return (
    <AutoClockInManagerInner 
      user={user} 
      primaryFacility={primaryFacility}
    />
  );
};

export default AutoClockInManager; 